import {Component, OnInit} from '@angular/core';
import {FormControl, UntypedFormBuilder} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {MatBottomSheet, MatBottomSheetConfig} from '@angular/material/bottom-sheet';
import {combineLatest, merge, Observable, of, Subject} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, filter, map, shareReplay, startWith, switchMap, take, tap} from 'rxjs/operators';
import {OnDestroyMixin, untilComponentDestroyed} from '@w11k/ngx-componentdestroyed';
import {AnalyticsServiceNameModel, TelemetryService} from 'telemetry-library';

import {HoldingStorageObjectReqRes} from 'src/app/shared/models/StorageObjectReqRes.model';
import {StorageObjectDataService} from 'src/app/services/shared/storage-object-data.service';
import {StorageObjectType} from 'src/app/shared/enums/StorageFileType.enum';
import {TerraUtils} from 'src/app/shared/TerraUtils';
import {FileOptionsMenuComponent} from './file-options-menu/file-options-menu.component';
import {FileMenuOption} from './FileMenuOption.enum';
import {LpHoldingService} from 'src/app/lp/shared/holdings/lp-holding.service';
import HoldingStatus from '../../../../../shared/enums/HoldingStatus.enum';
import {DialogService} from 'src/app/services/dialog.service';
import {ConfirmDialogParams} from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import {FilestackService} from '@filestack/angular';
import {ClientOptions, PickerOptions, Security} from 'filestack-js';
import {PickerInstance, PickerResponse, PickerStoreOptions} from 'filestack-js/build/main/lib/picker';
import {AppSettingsService} from 'src/app/services/app-settings.service';
import {AlertDialogParams} from 'src/app/shared/components/alert-dialog/alert-dialog.component';
import {SnackbarService} from 'src/app/services/snackbar.service';
import StorageObjectPermissionLevel from 'src/app/shared/enums/StorageObjectPermissionLevel.enum';
import {ImpersonationService} from '../../../../../services/shared/impersonation.service';
import {SelectAndAgreeComponent} from './lp-share-approve/lp-share-approve.component';
import {InvestingEntityReqRes} from 'src/app/dashboard/models/InvestingEntityReqRes.model';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {InvestingEntityShareService} from '../../../../investing-entity-share/services/investing-entity-share.service';
import {UserService} from '../../../../../services/shared/user.service';

interface IDocumentsFilters {
  searchTerm: string;
  period: SearchPeriods;
  order: OrderDocuments;
}

enum SearchPeriods {
  allTimes = 0,
  currentQuarter = 1,
  currentYear = 2,
  previousYear = 3
}

enum OrderDocuments {
  dateAsc = 0, // Older first
  dateDesc = 1 // Newer first
}

@Component({
  selector: 'terra-lp-documents-tab',
  templateUrl: './lp-documents-tab.component.html',
  styleUrls: ['../../../../styles/holding/holding-page-shared.scss', './lp-documents-tab.component.scss']
})
export class LpDocumentsTabComponent extends OnDestroyMixin implements OnInit {

  // enums
  StorageObjectType = StorageObjectType;
  OrderDocuments = OrderDocuments;

  filterForm = this.fb.group({
    searchTerm: '',
    period: SearchPeriods.allTimes,
    order: OrderDocuments.dateDesc
  });

  picker: PickerInstance;
  private investingEntityIdChosen: number;
  private investingEntities: InvestingEntityReqRes[];
  investingEntitiesRefresh$ = new Subject<InvestingEntityReqRes[]>();
  refreshData$: Subject<IDocumentsFilters> = new Subject<IDocumentsFilters>();
  isLoader$ = this.lpHoldingService.isLoaderDocument$.asObservable();
  private shareFileWithGp = false;
  private agentCompanyName = '';

  investedInHolding$ = combineLatest([this.investingEntityShareService.investingEntities$, this.investingEntitiesRefresh$]).pipe(
    map(([myIe, investingEntities]) => !!(myIe?.map(ie => ie.id).filter(id => investingEntities?.map(ie => ie.id).includes(id)).length)),
    shareReplay(1)
  );

  private filters$: Observable<IDocumentsFilters> = this.filterForm.valueChanges.pipe(
    debounceTime(200),
    startWith(this.filterForm.value),
    distinctUntilChanged());

  private updateDocument$ = merge(this.filters$, this.refreshData$);

  documents$ = this.updateDocument$.pipe(
    untilComponentDestroyed(this),
    switchMap(filters => {
      const fromAndToDates = this.generateFromAndToDate(filters.period);
      const orderBy = this.generateOrderBy(filters.order);
      const sortOrder = this.generateSortOrder(filters.order);
      return this.lpHoldingService.getDocuments(filters.searchTerm, fromAndToDates.fromDate, fromAndToDates.toDate, orderBy, sortOrder);
    }),
    map(searchResponse => searchResponse.rows.filter(so => so.storageObjectType === StorageObjectType.File)),
    catchError(err => {
      this.lpHoldingService.isLoaderDocument$.next(false);
      return of([] as HoldingStorageObjectReqRes[]);
    }),
    shareReplay(1));

  // The docId / storageObjectId may be in the query string,
  // but most likely the user will have a direct link to the media-preview page, so this logic will not be used.
  private previewFileOnload$ = this.activatedRoute.queryParams.pipe(
    map(params => {
      return +params.docId || +params.storageObjectId || null;
    }),
    filter(storageObjectId => storageObjectId !== null),
    switchMap(storageObjectId => this.storageObjectDataService.getFile(storageObjectId)),
    tap(storageObject => this.previewDocument(storageObject))
  );

  private generateFromAndToDate(period: SearchPeriods): { fromDate: Date, toDate: Date } {
    const currentDate = new Date();
    const response = {} as { fromDate: Date, toDate: Date };
    switch (period) {
      case SearchPeriods.allTimes:
        response.fromDate = null;
        response.toDate = null;
        break;
      case SearchPeriods.currentQuarter:
        const currentQuarterStartEnd = TerraUtils.getCurrentQuarterBoundaries();
        response.fromDate = currentQuarterStartEnd[0];
        break;
      case SearchPeriods.currentYear:
        response.fromDate = new Date(currentDate.getFullYear(), 0, 1);
        break;
      case SearchPeriods.previousYear:
        response.fromDate = new Date(currentDate.getFullYear() - 1, 0, 1);
        response.toDate = new Date(currentDate.getFullYear() - 1, 11, 31);
        break;
    }

    return response;
  }

  private generateOrderBy(order: OrderDocuments) {
    switch (order) {
      case OrderDocuments.dateAsc:
      case OrderDocuments.dateDesc:
        return 'CreateTimestamp';
      default:
        return 'CreateTimestamp';
    }
  }

  private generateSortOrder(order: OrderDocuments) {
    switch (order) {
      case OrderDocuments.dateAsc:
        return 'asc';
      case OrderDocuments.dateDesc:
        return 'desc';
      default:
        return 'asc';
    }
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private storageObjectDataService: StorageObjectDataService,
    private lpHoldingService: LpHoldingService,
    private fb: UntypedFormBuilder,
    private bottomSheet: MatBottomSheet,
    private telemetryService: TelemetryService,
    private dialogService: DialogService,
    private filestackService: FilestackService,
    private appSettingsService: AppSettingsService,
    private snackbarService: SnackbarService,
    private impersonationService: ImpersonationService,
    private investingEntityShareService: InvestingEntityShareService,
    private readonly dialog: MatDialog,
    private userService: UserService
  ) {
    super();
  }

  ngOnInit() {
    this.previewFileOnload$.pipe(untilComponentDestroyed(this)).subscribe();
    this.lpHoldingService.status$.pipe(take(1)).subscribe(status => {
      if (status === HoldingStatus.Fundraising) {
        this.lpHoldingService.holdingId$.pipe(take(1)).subscribe(holdingId => {
          this.telemetryService.create({
            eventID: '210',
            eventTitle: 'LP VIEWED PENDING INVESTMENT DOCUMENTS TAB',
            holdingID: holdingId,
            additional: {
              isMobileDevice: TerraUtils.isMobileDevice()
            }
          }, AnalyticsServiceNameModel.Insights);
        });
      }
    });

    this.lpHoldingService.investingEntities$.pipe(
      untilComponentDestroyed(this)
    ).subscribe(ies => {
        this.investingEntities = ies;
        this.investingEntitiesRefresh$.next(ies);
      }
    );
    this.lpHoldingService.holdingDetails$.pipe(
      untilComponentDestroyed(this),
      map(h => h.gpDetails.agentCompanyName)
    )
      .subscribe(
        name => this.agentCompanyName = name
      );
  }

  get isViewingAsInvestor(): boolean {
    return this.impersonationService.isViewingAsInvestor;
  }

  showFileOptionsMenu(storageObject: HoldingStorageObjectReqRes) {
    const config = new MatBottomSheetConfig();
    config.data = storageObject;
    this.bottomSheet.open<any, any, FileMenuOption>(FileOptionsMenuComponent, config).afterDismissed()
      .subscribe(selectedOption => {
        switch (selectedOption) {
          case FileMenuOption.Download:
            this.downloadDocument(storageObject);
            break;
          case FileMenuOption.View:
            this.previewDocument(storageObject);
            break;
          default:
            return;
        }
      });
  }

  previewDocument(fileToPreview: HoldingStorageObjectReqRes) {
    this.lpHoldingService.holdingId$.pipe(take(1)).subscribe(holdingId => {
      this.telemetryService.create({
        eventID: '202',
        eventTitle: 'LP LOOKED AT DOCUMENT IN PORTAL',
        holdingID: holdingId,
        additional: {
          documentName: fileToPreview.displayName,
          isMobileDevice: TerraUtils.isMobileDevice()
        }
      }, AnalyticsServiceNameModel.Insights);
    });

    this.lpHoldingService.previewHoldingDocument(fileToPreview);
  }

  downloadDocument(storageObject: HoldingStorageObjectReqRes) {
    this.storageObjectDataService.getDownloadToken(storageObject.id).subscribe();
  }

  delete(storageObject: HoldingStorageObjectReqRes) {
    if (this.hasDeleteAccess(storageObject)) {
      const dialogParams = new ConfirmDialogParams();
      dialogParams.actionLabel = `Delete`;
      dialogParams.title = `Delete item`;
      dialogParams.description = `<p>Are you sure you want to delete this item?</p><p>Item will be deleted immediately, and you cannot undo this action.</p>`;

      this.dialogService.confirmDialog(dialogParams).afterClosed()
        .pipe(
          untilComponentDestroyed(this),
          filter(isConfirmed => isConfirmed),
          switchMap(_ => this.storageObjectDataService.delete(storageObject.id))
        )
        .subscribe(
          () => {
            this.snackbarService.showGeneralMessage('Item deleted');
            this.refreshData$.next(this.filterForm.value);
          },
          err => {
            this.snackbarService.showGeneralMessage('Item deletion failed');
          }
        );
    }
  }

  hasDeleteAccess(storageObject: HoldingStorageObjectReqRes): Observable<boolean> {
    return this.userService.accountDetails$.pipe(
      map(ad => {
        if (storageObject.isPublic || storageObject.clientDetailsId !== ad.id) {
          return false;
        }
        if (storageObject.storageObjectPermissions.length === 0) {
          return true;
        }

        if (storageObject.storageObjectPermissions.length === 1
          && (storageObject.storageObjectPermissions[0].permissionLevel === StorageObjectPermissionLevel.View + StorageObjectPermissionLevel.Edit
            || storageObject.storageObjectPermissions[0].permissionLevel === StorageObjectPermissionLevel.Edit)) {
          return true;
        }

        return false;
      }),
      shareReplay(1)
    );
  }

  private generateInvestingEntityOptions() {
    return this.investingEntities.map(({id, name, investingEntityType}) => ({
      id,
      name,
      email: '',
      type: investingEntityType
    }));
  }

  openDialogToUploadFile() {
    const config: MatDialogConfig = {
      disableClose: true,
      // width: '500px',
      data: {
        title: 'Select the investing entity associated with the uploaded files',
        confirmButton: 'Next',
        options: this.generateInvestingEntityOptions(),
        checkboxLabel: `Share documents with the ${this.agentCompanyName}`,
        checkboxDisabled: true,
        dropdownPlaceholder: 'Select an Option'
      },
    };

    const dialogRef = this.dialog.open(SelectAndAgreeComponent, config);

    return dialogRef.afterClosed().subscribe(
      (investingEntitySelected: FormControl) => {
        if (investingEntitySelected) {
          this.investingEntityIdChosen = investingEntitySelected.value.id;
          this.openFilePicker();
        }
      }
    );
  }

  openFilePicker() {
    // When we open the file picker, first we want to get the most updadted quota,
    // in case it was changed in another tab or by another user in the organization
    this.lpHoldingService.getQuotaRefreshed().pipe(take(1))
      .subscribe(quotaDetails => {
        if (quotaDetails.quotaUsedMB >= quotaDetails.maxStorageQuotaMB ||
          quotaDetails.maxStorageFilesCount - quotaDetails.currentStorageFilesCount <= 0) {
          this.dialogService.alertDialog(new AlertDialogParams(
            'Storage is full',
            `<p>You have reached your storage limit.</p>
            For more space, <a target="_blank" class="light link" href="//www.covercy.com/contact-us">contact support</a>`
          ));
        } else {
          this.lpHoldingService.getFilestackPolicy()
            .pipe(take(1))
            .subscribe(policy => {
              const pickerOptions: PickerOptions = {
                onUploadDone: (event) => this.onUploadDone(event),
                onFileUploadFailed: (event, error) => this.onUploadError(error),
                // images larger than this size will be resized by the picker
                imageMax: [1920, 1920],
                maxFiles: quotaDetails.maxStorageFilesCount - quotaDetails.currentStorageFilesCount,
                maxSize: 500 * 1024 * 1024, // 500MB
                // accept: ['image/*', 'video/*', 'audio/*', 'text/*', '.pdf', '.doc', '.docx', '.docm', '.ppt', '.pptx', '.pptm', '.xls', '.xlsx', '.xlsm', '.odt', '.ods', '.odp'],
                accept: [
                  '.jpg', '.jpeg', '.png', '.tif', '.tiff', '.gif', '.bmp',
                  '.mp3', '.ogg', '.wav', '.wma', '.flac', '.alac',
                  '.mp4', '.webm', '.avi', '.mpeg', '.wmv', '.ogg',
                  '.pdf', '.doc', '.docx', '.docm', '.odt', '.txt',
                  '.ppt', '.pptx', '.pptm', '.odp',
                  '.xls', '.xlsx', '.xlsm', '.ods', '.csv'],
                fromSources: [ 'local_file_system' ],
                storeTo: {
                  location: 'azure',
                  container: 'main',
                } as PickerStoreOptions
              } as PickerOptions;

              const filestackClientOptions = {
                security: {
                  policy: policy.filestackPolicy,
                  signature: policy.filestackSignature
                } as Security
              } as ClientOptions;

              this.filestackService.init(this.appSettingsService.fileStackApiKey, filestackClientOptions);
              this.picker = this.filestackService.picker(pickerOptions);
              this.picker.open();
            });
        }
      });
  }

  onUploadDone(event: PickerResponse) {
    this.createFile(event);
  }

  onUploadError(error: any) {
    console.log('onUploadError: ', error);
  }

  createFile(pickerResponse: PickerResponse) {
    if (pickerResponse.filesFailed.length > 0) {
      this.snackbarService.showGeneralMessage('Error: Failed to save uploaded files');
      return;
    }

    this.lpHoldingService.createFiles(pickerResponse, this.investingEntityIdChosen).subscribe(resultFiles => {
      // update the table:
      if (resultFiles.some(file => file.isDeleted)) {
        const params = new AlertDialogParams();
        params.title = 'Some of the files were not uploaded';
        params.description = `<p>You have exceeded your storage limit. The following files were not uploaded:</p>
        <ul class="validation-error">`;
        resultFiles.filter(file => file.isDeleted).forEach(deletedFile => {
          params.description += `<li>${deletedFile.displayName}.${deletedFile.extension}</li>`;
        });
        params.description += '</ul>';

        this.dialogService.alertDialog(params);
      } else {
        this.refreshData$.next(this.filterForm.value);
        this.snackbarService.showGeneralMessage(resultFiles.length > 1 ? 'Files uploaded' : 'File uploaded');
      }
    }, error => {
      this.snackbarService.showGeneralMessage('Error: Failed to upload file(s)');
    });
  }
}
